import { api } from "@breakout/backend/convex/_generated/api"
import type { Id } from "@breakout/backend/convex/_generated/dataModel"
import {
	type AccumulatedFees,
	batchExecute,
	encodeFinalize,
	encodeResolve,
	encodeSweepProtocolFees,
	encodeSweepReserve,
	formatUsdc,
	getExplorerTxUrl,
	getFees,
	getMarketBalancesBatch,
	getResolutionsBatch,
	ipfsUri,
	type SafeTransaction,
	serializeSafeBatch,
	sweepProtocolFees,
	toSafeTransactionBatch,
	truncateAddress,
} from "@breakout/sdk"
import {
	formatDateSec,
	formatDateTimeMs,
	formatDateTimeSec,
	isPastSec,
	nowSec,
} from "@breakout/shared/utils/time"
import { toast } from "@breakout/ui/components/Toast"
import { cn } from "@breakout/ui/utils"
import { convexQuery } from "@convex-dev/react-query"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { zodSearchValidator } from "@tanstack/router-zod-adapter"
import { useMutation as useConvexMutation } from "convex/react"
import type { FunctionReturnType } from "convex/server"
import {
	ArrowLeft,
	Camera,
	CheckCircle,
	CheckCircle2,
	Clipboard,
	DollarSign,
	Download,
	ExternalLink,
	FileCheck,
	Plus,
	RefreshCw,
	XCircle,
} from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { type Address, getAddress } from "viem"
import { useAccount, useWalletClient } from "wagmi"
import { z } from "zod"
import { AddLiquidityDialog } from "../components/AddLiquidityDialog"
import { Skeleton } from "../components/skeletons/Skeleton"
import { TableSkeleton } from "../components/skeletons/TableRowSkeleton"
import {
	type TransactionStatus,
	TransactionStatusDialog,
} from "../components/TransactionStatusDialog"
import { useAdminAuth } from "../hooks/useAdminAuth"
import { useExecutionMode } from "../hooks/useExecutionMode"
import { useRegistryConfig } from "../hooks/useRegistryConfig"
import { useAdminConfig } from "../lib/providers"

// Types
type ResolutionPreview = FunctionReturnType<
	typeof api.domains.admin.resolution.getResolutionPreview
>
type MarketResolution = NonNullable<ResolutionPreview>["markets"][number]

type OnChainState = {
	isResolved: boolean
	isFinalized: boolean
	winningOutcome: number
	nonce: number
	loading: boolean
	error?: string
}

type TxDialogState = {
	open: boolean
	status: TransactionStatus
	hash?: string
	title: string
	description: string
	errorMessage: string
	operationCount: number
}

const initialTxDialogState: TxDialogState = {
	open: false,
	status: "idle",
	hash: undefined,
	title: "",
	description: "",
	errorMessage: "",
	operationCount: 1,
}

interface MarketFeeData {
	marketAddress: string
	displayName: string
	fees: AccumulatedFees | null
}

interface MarketReserveData {
	marketAddress: string
	displayName: string
	isFinalized: boolean
	balance: number | null
}

// Route definition
const weekSearchSchema = z.object({
	tab: z.enum(["overview", "markets", "resolution", "fees"]).optional().catch(undefined),
})

type TabId = "overview" | "markets" | "resolution" | "fees"

function PendingComponent() {
	return (
		<div className="space-y-6">
			{/* Header skeleton */}
			<div className="flex items-center gap-4">
				<Skeleton className="size-5" />
				<div>
					<Skeleton className="mb-2 h-8 w-64" />
					<div className="flex items-center gap-3">
						<Skeleton className="h-5 w-16 rounded-full" />
						<Skeleton className="h-4 w-24" />
						<Skeleton className="h-4 w-48" />
					</div>
				</div>
			</div>

			{/* Mode indicator skeleton */}
			<Skeleton className="h-12 w-full rounded-lg" />

			{/* Tabs skeleton */}
			<Skeleton className="h-12 w-full rounded-lg" />

			{/* Content skeleton */}
			<div className="space-y-4">
				<Skeleton className="h-64 w-full rounded-lg" />
				<Skeleton className="h-32 w-full rounded-lg" />
			</div>
		</div>
	)
}

export const Route = createFileRoute("/weeks/$weekId")({
	component: WeekHubPage,
	validateSearch: zodSearchValidator(weekSearchSchema),
	pendingComponent: PendingComponent,
	// Loader ensures params are resolved before component renders
	// Also required for pendingComponent to show during navigation
	loader: ({ params }) => {
		// Validate weekId exists - if not, this will be caught by error boundary
		if (!params.weekId) {
			throw new Error("Week ID is required")
		}
		return { weekId: params.weekId }
	},
})

function WeekHubPage() {
	const { weekId } = Route.useParams()
	const { tab } = Route.useSearch()
	const navigate = useNavigate()
	const { isConnected } = useAccount()
	const { isHotWalletMode } = useExecutionMode()

	const activeTab = tab ?? "overview"

	const setActiveTab = useCallback(
		(newTab: TabId) => {
			void navigate({
				to: "/weeks/$weekId",
				params: { weekId },
				search: { tab: newTab === "overview" ? undefined : newTab },
				replace: true,
			})
		},
		[navigate, weekId],
	)

	// Fetch week details
	const { data: weeks, isLoading: weeksLoading } = useQuery({
		...convexQuery(api.domains.admin.queries.listWeeks, {}),
		enabled: isConnected,
	})

	const week = weeks?.find(w => w.weekId === weekId)

	if (!isConnected) {
		return (
			<div className="py-20 text-center">
				<p className="text-gray-400">Connect your wallet to access this page.</p>
			</div>
		)
	}

	if (weeksLoading) {
		return (
			<div className="py-20 text-center">
				<div className="mx-auto size-8 animate-spin rounded-full border-2 border-gray-600 border-t-gray-300" />
				<p className="mt-4 text-gray-500">Loading week data...</p>
			</div>
		)
	}

	if (!week) {
		return (
			<div className="py-20 text-center">
				<p className="text-gray-400">Week not found: {weekId.slice(0, 12)}...</p>
				<Link className="mt-4 inline-block text-gray-400 hover:text-gray-300" to="/">
					Back to Dashboard
				</Link>
			</div>
		)
	}

	const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
		{
			id: "overview",
			label: "Overview",
			icon: <FileCheck aria-hidden="true" className="size-4" />,
		},
		{
			id: "markets",
			label: "Markets",
			icon: <CheckCircle aria-hidden="true" className="size-4" />,
		},
		{
			id: "resolution",
			label: "Resolution",
			icon: <Camera aria-hidden="true" className="size-4" />,
		},
		{ id: "fees", label: "Fees", icon: <DollarSign aria-hidden="true" className="size-4" /> },
	]

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Link aria-label="Back to dashboard" className="text-gray-400 hover:text-white" to="/">
					<ArrowLeft aria-hidden="true" className="size-5" />
				</Link>
				<div>
					<h1 className="font-bold text-2xl">Week: {weekId.slice(0, 12)}...</h1>
					<div className="mt-1 flex items-center gap-3">
						<StatusBadge status={week.status} />
						<span className="text-gray-500 text-sm">{week.marketCount} markets</span>
						<span className="text-gray-500 text-sm">
							{formatDateSec(week.openTimeSec)} - {formatDateSec(week.cutoffTimeSec)}
						</span>
					</div>
				</div>
			</div>

			{/* Mode Indicator */}
			<div className="flex items-center gap-4 rounded-lg border border-gray-800 bg-gray-900 px-4 py-3">
				<span className="text-gray-500 text-sm">Mode:</span>
				<span className={cn("font-medium", isHotWalletMode ? "text-green-400" : "text-blue-400")}>
					{isHotWalletMode ? "Hot Wallet (Direct Execution)" : "Safe Batch (Generate JSON)"}
				</span>
			</div>

			{/* Tabs */}
			<div className="flex gap-1 rounded-lg bg-gray-900 p-1">
				{tabs.map(tab => (
					<button
						className={cn(
							"flex items-center gap-2 rounded-md px-4 py-2 font-medium text-sm transition",
							activeTab === tab.id ? "bg-gray-800 text-white" : "text-gray-400 hover:text-gray-200",
						)}
						key={tab.id}
						onClick={() => setActiveTab(tab.id)}
						type="button"
					>
						{tab.icon}
						{tab.label}
					</button>
				))}
			</div>

			{/* Tab Content */}
			{activeTab === "overview" && (
				<OverviewTab setActiveTab={setActiveTab} week={week} weekId={weekId} />
			)}
			{activeTab === "markets" && <MarketsTab weekId={weekId} />}
			{activeTab === "resolution" && <ResolutionTab weekId={weekId} />}
			{activeTab === "fees" && <FeesTab weekId={weekId} />}
		</div>
	)
}

// ============================================================================
// Overview Tab
// ============================================================================

interface Week {
	weekId: string
	status: string
	marketCount: number
	openTimeSec: number
	cutoffTimeSec: number
	hasStartSnapshot: boolean
	hasEndSnapshot: boolean
}

function OverviewTab({
	week,
	weekId,
	setActiveTab,
}: {
	week: Week
	weekId: string
	setActiveTab: (tab: TabId) => void
}) {
	const currentSec = nowSec()
	const isPastCutoff = isPastSec(week.cutoffTimeSec)
	const isActive = currentSec >= week.openTimeSec && currentSec < week.cutoffTimeSec

	// Fetch resolution preview for status
	const { data: preview } = useQuery({
		...convexQuery(api.domains.admin.resolution.getResolutionPreview, { weekId }),
		enabled: true,
	})

	// Define lifecycle stages
	const stages = [
		{
			id: "created",
			label: "Week Created",
			complete: true,
			description: "Week and markets deployed on-chain",
		},
		{
			id: "snapshot_start",
			label: "Start Snapshot",
			complete: week.hasStartSnapshot,
			description: week.hasStartSnapshot
				? "Captured and stored on IPFS"
				: "Will be captured at launch",
		},
		{
			id: "active",
			label: "Trading Active",
			complete: isPastCutoff,
			active: isActive,
			description: isActive
				? "Trading is open"
				: isPastCutoff
					? "Trading period ended"
					: `Opens ${formatDateTimeSec(week.openTimeSec)}`,
		},
		{
			id: "cutoff",
			label: "Trading Closed",
			complete: isPastCutoff,
			description: `Cutoff: ${formatDateTimeSec(week.cutoffTimeSec)}`,
		},
		{
			id: "snapshot_end",
			label: "End Snapshot",
			complete: week.hasEndSnapshot,
			description: week.hasEndSnapshot
				? "Captured - ready for resolution"
				: isPastCutoff
					? "Needs to be captured"
					: "Will be captured after cutoff",
		},
		{
			id: "resolved",
			label: "Markets Resolved",
			complete: week.status === "resolved" || week.status === "finalized",
			description:
				week.status === "resolved" || week.status === "finalized"
					? "All markets resolved on-chain"
					: "Submit resolutions on-chain",
		},
		{
			id: "finalized",
			label: "Markets Finalized",
			complete: week.status === "finalized",
			description: week.status === "finalized" ? "Claims are enabled" : "Finalize to enable claims",
		},
	]

	return (
		<div className="space-y-6">
			{/* Lifecycle Checklist */}
			<div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
				<h2 className="mb-4 font-semibold text-lg">Lifecycle Progress</h2>
				<div className="space-y-4">
					{stages.map((stage, i) => (
						<div className="flex items-start gap-4" key={stage.id}>
							<div className="flex flex-col items-center">
								<div
									className={`flex size-8 items-center justify-center rounded-full ${
										stage.complete
											? "bg-green-500/20 text-green-400"
											: stage.active
												? "bg-blue-500/20 text-blue-400"
												: "bg-gray-700 text-gray-500"
									}`}
								>
									{stage.complete ? (
										<CheckCircle aria-hidden="true" className="size-5" />
									) : (
										<span className="font-medium text-sm">{i + 1}</span>
									)}
								</div>
								{i < stages.length - 1 && (
									<div
										className={cn("h-8 w-0.5", stage.complete ? "bg-green-500/50" : "bg-gray-700")}
									/>
								)}
							</div>
							<div className="flex-1">
								<div
									className={cn(
										"font-medium",
										stage.complete
											? "text-green-400"
											: stage.active
												? "text-blue-400"
												: "text-gray-400",
									)}
								>
									{stage.label}
								</div>
								<div className="text-gray-500 text-sm">{stage.description}</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Snapshot Info */}
			<div className="grid gap-4 md:grid-cols-2">
				<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
					<div className="mb-2 text-gray-500 text-sm">Start Snapshot</div>
					{preview?.startSnapshot ? (
						<div className="space-y-1">
							<div className="text-gray-500 text-sm">
								{formatDateTimeMs(preview.startSnapshot.timestampMs)}
							</div>
							{preview.startSnapshot.ipfsCid && (
								<a
									className="text-gray-400 text-xs hover:text-gray-300"
									href={`https://ipfs.io/ipfs/${preview.startSnapshot.ipfsCid}`}
									rel="noopener noreferrer"
									target="_blank"
								>
									View on IPFS
								</a>
							)}
						</div>
					) : (
						<div className="text-gray-600">Not linked</div>
					)}
				</div>
				<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
					<div className="mb-2 flex items-center justify-between">
						<span className="text-gray-500 text-sm">End Snapshot</span>
						{preview?.endSnapshot && (
							<button
								className="text-gray-400 text-xs hover:text-gray-300"
								onClick={() => setActiveTab("resolution")}
								type="button"
							>
								Change
							</button>
						)}
					</div>
					{preview?.endSnapshot ? (
						<div className="space-y-1">
							<div className="text-gray-500 text-sm">
								{formatDateTimeMs(preview.endSnapshot.timestampMs)}
							</div>
							{preview.endSnapshot.ipfsCid && (
								<a
									className="text-gray-400 text-xs hover:text-gray-300"
									href={`https://ipfs.io/ipfs/${preview.endSnapshot.ipfsCid}`}
									rel="noopener noreferrer"
									target="_blank"
								>
									View on IPFS
								</a>
							)}
						</div>
					) : (
						<div className="text-yellow-500">
							{isPastCutoff ? "Needs to be captured" : "Pending (after cutoff)"}
						</div>
					)}
				</div>
			</div>

			{/* Week Details */}
			<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
				<h3 className="mb-3 font-medium">Week Details</h3>
				<dl className="grid gap-2 text-sm sm:grid-cols-2">
					<div>
						<dt className="text-gray-500">Week ID</dt>
						<dd className="font-mono">{weekId}</dd>
					</div>
					<div>
						<dt className="text-gray-500">Status</dt>
						<dd>
							<StatusBadge status={week.status} />
						</dd>
					</div>
					<div>
						<dt className="text-gray-500">Open Time</dt>
						<dd>{formatDateTimeSec(week.openTimeSec)}</dd>
					</div>
					<div>
						<dt className="text-gray-500">Cutoff Time</dt>
						<dd>{formatDateTimeSec(week.cutoffTimeSec)}</dd>
					</div>
					<div>
						<dt className="text-gray-500">Market Count</dt>
						<dd>{week.marketCount}</dd>
					</div>
				</dl>
			</div>
		</div>
	)
}

// ============================================================================
// Markets Tab
// ============================================================================

function MarketsTab({ weekId }: { weekId: string }) {
	const { isConnected } = useAccount()
	const { publicClient, chainId } = useAdminConfig()

	// Fetch markets
	const { data: markets, isLoading } = useQuery({
		...convexQuery(api.domains.markets.queries.list, { weekId }),
		enabled: isConnected,
	})

	// Fetch on-chain states
	const [onChainStates, setOnChainStates] = useState<Map<string, OnChainState>>(new Map())
	const [isLoadingStates, setIsLoadingStates] = useState(false)

	// Market balances
	const [marketBalances, setMarketBalances] = useState<Map<string, number>>(new Map())
	const [isLoadingBalances, setIsLoadingBalances] = useState(false)

	// Add liquidity dialog state
	const [selectedMarket, setSelectedMarket] = useState<{
		address: Address
		name: string
		virtualLiquidityUsdc: number
	} | null>(null)

	const fetchOnChainStates = useCallback(async () => {
		if (!markets || markets.length === 0) {
			return
		}

		setIsLoadingStates(true)
		const marketsWithAddresses = markets.filter(m => m.marketAddress)

		if (marketsWithAddresses.length === 0) {
			setIsLoadingStates(false)
			return
		}

		try {
			// SDK normalizes addresses to lowercase, so use lowercase for lookups
			const addresses = marketsWithAddresses.map(m => getAddress(m.marketAddress))

			const { resolutions, errors } = await getResolutionsBatch(publicClient, {
				markets: addresses,
			})

			const newStates = new Map<string, OnChainState>()
			for (const market of markets) {
				if (!market.marketAddress) {
					continue
				}
				// SDK stores results with lowercase keys, so use lowercase for lookup
				const normalizedAddress = market.marketAddress.toLowerCase()
				const resolution = resolutions.get(normalizedAddress)
				const error = errors.get(normalizedAddress)

				if (resolution) {
					newStates.set(market.marketAddress, {
						isResolved: resolution.isResolved,
						isFinalized: resolution.isFinalized,
						winningOutcome: resolution.winningOutcome,
						nonce: resolution.nonce,
						loading: false,
					})
				} else {
					newStates.set(market.marketAddress, {
						isResolved: false,
						isFinalized: false,
						winningOutcome: 0,
						nonce: 0,
						loading: false,
						error: error ?? "Failed to fetch",
					})
				}
			}
			setOnChainStates(newStates)
		} catch (err) {
			console.error("Failed to fetch on-chain states:", err)
			toast.error(err instanceof Error ? err.message : "Failed to fetch on-chain states")
			// Set error state for all markets when bulk fetch fails
			const errorStates = new Map<string, OnChainState>()
			for (const market of markets) {
				if (market.marketAddress) {
					errorStates.set(market.marketAddress, {
						isResolved: false,
						isFinalized: false,
						winningOutcome: 0,
						nonce: 0,
						loading: false,
						error: "RPC error",
					})
				}
			}
			setOnChainStates(errorStates)
		} finally {
			setIsLoadingStates(false)
		}
	}, [markets, publicClient])

	const fetchMarketBalances = useCallback(async () => {
		if (!markets || markets.length === 0) {
			return
		}

		setIsLoadingBalances(true)
		const marketsWithAddresses = markets.filter(m => m.marketAddress)

		if (marketsWithAddresses.length === 0) {
			setIsLoadingBalances(false)
			return
		}

		try {
			const addresses = marketsWithAddresses.map(m => getAddress(m.marketAddress))
			const { balances, errors } = await getMarketBalancesBatch(publicClient, {
				markets: addresses,
			})

			const newBalances = new Map<string, number>()
			for (const m of marketsWithAddresses) {
				const normalized = m.marketAddress.toLowerCase()
				const info = balances.get(normalized)
				if (info) {
					newBalances.set(m.marketAddress, info.balance)
				}
				const error = errors.get(normalized)
				if (error) {
					console.error(`[balances] Failed to read balance for ${m.marketAddress}:`, error)
				}
			}
			setMarketBalances(newBalances)
		} catch (err) {
			console.error("Failed to fetch market balances:", err)
			toast.error(err instanceof Error ? err.message : "Failed to fetch market balances")
		} finally {
			setIsLoadingBalances(false)
		}
	}, [markets, publicClient])

	useEffect(() => {
		if (markets && markets.length > 0) {
			fetchOnChainStates().catch(err => console.error("Failed to fetch on-chain states:", err))
			fetchMarketBalances().catch(err => console.error("Failed to fetch market balances:", err))
		}
	}, [markets, fetchOnChainStates, fetchMarketBalances])

	const handleCloseDialog = useCallback(() => {
		setSelectedMarket(null)
		// Refetch balances after dialog closes
		fetchMarketBalances().catch(err => console.error("Failed to refetch balances:", err))
	}, [fetchMarketBalances])

	if (isLoading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-10 w-48 rounded-lg" />
				<div className="rounded-lg border border-gray-800 bg-gray-900">
					<table className="w-full text-left text-sm">
						<thead>
							<tr className="border-gray-800 border-b text-gray-500">
								<th className="px-4 py-3">Rank</th>
								<th className="px-4 py-3">Market</th>
								<th className="px-4 py-3">Type</th>
								<th className="px-4 py-3">On-Chain Status</th>
								<th className="px-4 py-3">Liquidity</th>
								<th className="px-4 py-3">Address</th>
							</tr>
						</thead>
						<tbody>
							<TableSkeleton columns={6} rows={10} />
						</tbody>
					</table>
				</div>
			</div>
		)
	}

	if (!markets || markets.length === 0) {
		return (
			<div className="rounded-lg border border-gray-800 bg-gray-900 p-6 text-center text-gray-500">
				No markets found for this week
			</div>
		)
	}

	return (
		<div className="space-y-4">
			{/* Actions */}
			<div className="flex items-center gap-4">
				<button
					aria-busy={isLoadingStates || isLoadingBalances}
					className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-gray-300 text-sm hover:bg-gray-700 disabled:opacity-50"
					disabled={isLoadingStates || isLoadingBalances}
					onClick={() => {
						fetchOnChainStates().catch(err => toast.error(err.message))
						fetchMarketBalances().catch(err => toast.error(err.message))
					}}
					type="button"
				>
					{isLoadingStates || isLoadingBalances ? (
						<>
							<div
								aria-hidden="true"
								className="size-4 animate-spin rounded-full border-2 border-gray-600 border-t-gray-300"
							/>
							Refreshing...
						</>
					) : (
						<>
							<RefreshCw aria-hidden="true" className="size-4" />
							Refresh Data
						</>
					)}
				</button>
			</div>

			{/* Markets Table */}
			<div className="rounded-lg border border-gray-800 bg-gray-900">
				<div className="overflow-x-auto">
					<table className="w-full text-left text-sm">
						<thead>
							<tr className="border-gray-800 border-b text-gray-500">
								<th className="px-4 py-3">Rank</th>
								<th className="px-4 py-3">Market</th>
								<th className="px-4 py-3">Type</th>
								<th className="px-4 py-3">On-Chain Status</th>
								<th className="px-4 py-3">Liquidity</th>
								<th className="px-4 py-3">Address</th>
							</tr>
						</thead>
						<tbody>
							{markets.map(market => {
								const state = onChainStates.get(market.marketAddress ?? "")
								const rawBalance = marketBalances.get(market.marketAddress ?? "")
								const virtualLiq = market.virtualLiquidityUsdc ?? 0
								const balance = rawBalance !== undefined ? rawBalance + virtualLiq : undefined
								const isDeployed = state && !state.error
								return (
									<tr
										className="border-gray-800 border-b hover:bg-gray-800/50"
										key={market.entityKey}
									>
										<td className="px-4 py-3 font-semibold">
											{market.startRank ? `#${market.startRank}` : "-"}
										</td>
										<td className="px-4 py-3">
											<div className="flex items-center gap-3">
												{market.avatarUrl && (
													<img
														alt={market.displayName ?? ""}
														className="size-8 rounded-full object-cover"
														decoding="async"
														height={32}
														loading="lazy"
														referrerPolicy="no-referrer"
														src={market.avatarUrl}
														width={32}
													/>
												)}
												<div>
													<div className="font-medium">{market.displayName || market.handle}</div>
													{market.handle && (
														<div className="text-gray-500 text-xs">@{market.handle}</div>
													)}
												</div>
											</div>
										</td>
										<td className="px-4 py-3">
											<span
												className={cn(
													"rounded px-2 py-0.5 text-xs",
													market.marketType === "fall_from_grace"
														? "bg-orange-500/20 text-orange-400"
														: "bg-gray-500/20 text-gray-400",
												)}
											>
												{market.marketType === "fall_from_grace" ? "FFG" : "Binary"}
											</span>
										</td>
										<td className="px-4 py-3">
											{state?.loading ? (
												<span className="text-gray-500 text-xs">Loading...</span>
											) : state?.error ? (
												<span
													className="rounded bg-gray-500/20 px-2 py-0.5 text-gray-400 text-xs"
													title={state.error}
												>
													Not Deployed
												</span>
											) : state ? (
												<div className="space-y-1">
													{state.isFinalized ? (
														<span className="rounded bg-green-500/20 px-2 py-0.5 text-green-400 text-xs">
															Finalized
														</span>
													) : state.isResolved ? (
														<span className="rounded bg-blue-500/20 px-2 py-0.5 text-blue-400 text-xs">
															Resolved (n={state.nonce})
														</span>
													) : (
														<span className="rounded bg-yellow-500/20 px-2 py-0.5 text-xs text-yellow-400">
															Awaiting Resolution
														</span>
													)}
												</div>
											) : (
												<span className="text-gray-500 text-xs">Click Refresh</span>
											)}
										</td>
										<td className="px-4 py-3">
											<div className="flex items-center gap-2">
												{isLoadingBalances ? (
													<span className="inline-block h-5 w-16 animate-pulse rounded bg-gray-700" />
												) : balance !== undefined ? (
													<span className="font-mono text-sm">{formatUsdc(balance)}</span>
												) : (
													<span className="text-gray-500 text-xs">-</span>
												)}
												{market.marketAddress && isDeployed && !state?.isFinalized && (
													<button
														aria-label={`Add liquidity to ${market.displayName || market.handle}`}
														className="rounded p-1 text-gray-400 hover:bg-gray-700 hover:text-white"
														onClick={() =>
															setSelectedMarket({
																address: getAddress(market.marketAddress),
																name: market.displayName || market.handle || "Market",
																virtualLiquidityUsdc: virtualLiq,
															})
														}
														type="button"
													>
														<Plus className="size-4" />
													</button>
												)}
											</div>
										</td>
										<td className="px-4 py-3">
											{market.marketAddress && (
												<a
													className="font-mono text-gray-400 text-xs hover:text-gray-300"
													href={getExplorerTxUrl(chainId, market.marketAddress)}
													rel="noopener noreferrer"
													target="_blank"
												>
													{truncateAddress(market.marketAddress, 6)}
												</a>
											)}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>

			{/* Add Liquidity Dialog */}
			{selectedMarket && (
				<AddLiquidityDialog
					marketAddress={selectedMarket.address}
					marketName={selectedMarket.name}
					onClose={handleCloseDialog}
					virtualLiquidityUsdc={selectedMarket.virtualLiquidityUsdc}
				/>
			)}
		</div>
	)
}

// ============================================================================
// Resolution Tab (Wizard)
// ============================================================================

function ResolutionTab({ weekId }: { weekId: string }) {
	const { isConnected } = useAccount()
	const { data: walletClient } = useWalletClient()
	const { chainId, publicClient } = useAdminConfig()
	const { operator: onChainOperator, resolver: onChainResolver } = useRegistryConfig()
	const { isHotWalletMode, isSafeMode } = useExecutionMode()
	const { password: adminPassword } = useAdminAuth()
	const queryClient = useQueryClient()

	const [wizardStep, setWizardStep] = useState<1 | 2 | 3 | 4>(1)
	const [showSnapshotSelector, setShowSnapshotSelector] = useState(false)
	const [isResolvingAll, setIsResolvingAll] = useState(false)
	const [isFinalizingAll, setIsFinalizingAll] = useState(false)
	const [txDialog, setTxDialog] = useState<TxDialogState>(initialTxDialogState)
	const [showConfirmDialog, setShowConfirmDialog] = useState<"resolve" | "finalize" | null>(null)

	// On-chain state for markets
	const [onChainStates, setOnChainStates] = useState<Map<string, OnChainState>>(new Map())
	const [isLoadingOnChainState, setIsLoadingOnChainState] = useState(false)

	// Fetch resolution preview
	const { data: preview, isLoading: previewLoading } = useQuery({
		...convexQuery(api.domains.admin.resolution.getResolutionPreview, { weekId }),
		enabled: isConnected,
	})

	// Fetch snapshots
	const { data: snapshots } = useQuery({
		...convexQuery(api.domains.admin.resolution.listSnapshots, { limit: 20 }),
		enabled: isConnected,
	})

	// Prepare resolution mutation
	const prepareResolutionMutation = useConvexMutation(
		api.domains.admin.resolution.prepareResolution,
	)
	const recordResolutionTxBatchMutation = useConvexMutation(
		api.domains.admin.resolution.recordResolutionTxBatch,
	)
	const recordFinalizationTxBatchMutation = useConvexMutation(
		api.domains.admin.resolution.recordFinalizationTxBatch,
	)

	// Fetch on-chain state
	const fetchOnChainStates = useCallback(
		async (markets: MarketResolution[]) => {
			if (!markets.length) {
				return
			}

			setIsLoadingOnChainState(true)
			const loadingStates = new Map<string, OnChainState>()
			for (const market of markets) {
				loadingStates.set(market.marketAddress, {
					isResolved: false,
					isFinalized: false,
					winningOutcome: 0,
					nonce: 0,
					loading: true,
				})
			}
			setOnChainStates(loadingStates)

			try {
				// SDK normalizes addresses to lowercase, so use lowercase for lookups
				const addresses = markets.map(m => getAddress(m.marketAddress))
				const { resolutions, errors } = await getResolutionsBatch(publicClient, {
					markets: addresses,
				})

				const newStates = new Map<string, OnChainState>()
				for (const market of markets) {
					// SDK stores results with lowercase keys
					const normalizedAddress = market.marketAddress.toLowerCase()
					const resolution = resolutions.get(normalizedAddress)
					const error = errors.get(normalizedAddress)

					if (resolution) {
						newStates.set(market.marketAddress, {
							isResolved: resolution.isResolved,
							isFinalized: resolution.isFinalized,
							winningOutcome: resolution.winningOutcome,
							nonce: resolution.nonce,
							loading: false,
						})
					} else {
						newStates.set(market.marketAddress, {
							isResolved: false,
							isFinalized: false,
							winningOutcome: 0,
							nonce: 0,
							loading: false,
							error: error ?? "Failed to fetch",
						})
					}
				}

				setOnChainStates(newStates)
			} catch (err) {
				console.error("Failed to fetch on-chain states:", err)
				// Set error state for all markets
				const errorStates = new Map<string, OnChainState>()
				for (const market of markets) {
					errorStates.set(market.marketAddress, {
						isResolved: false,
						isFinalized: false,
						winningOutcome: 0,
						nonce: 0,
						loading: false,
						error: err instanceof Error ? err.message : "RPC error",
					})
				}
				setOnChainStates(errorStates)
			} finally {
				setIsLoadingOnChainState(false)
			}
		},
		[publicClient],
	)

	useEffect(() => {
		if (preview?.markets?.length && preview.isReady) {
			fetchOnChainStates(preview.markets).catch(err =>
				console.error("Failed to fetch on-chain states:", err),
			)
		}
	}, [preview?.markets, preview?.isReady, fetchOnChainStates])

	// Auto-advance wizard step based on state
	useEffect(() => {
		if (!preview) {
			return
		}

		if (!preview.endSnapshot) {
			setWizardStep(1)
		} else if (preview.isReady) {
			// Guard against vacuous truth: .every() on empty array returns true
			const hasStates = onChainStates.size > 0
			const allResolved = hasStates && [...onChainStates.values()].every(s => s.isResolved)
			const allFinalized = hasStates && [...onChainStates.values()].every(s => s.isFinalized)

			if (allFinalized) {
				setWizardStep(4)
			} else if (allResolved) {
				setWizardStep(3)
			} else if (preview.endSnapshot) {
				setWizardStep(2)
			}
		}
	}, [preview, onChainStates])

	// Handle prepare resolution
	const handlePrepareResolution = async (snapshotId: Id<"snapshots">) => {
		if (!adminPassword) {
			toast.error("Admin password required. Please authenticate first.")
			return
		}
		try {
			await prepareResolutionMutation({ adminPassword, weekId, endSnapshotId: snapshotId })
			queryClient.invalidateQueries()
			toast.success("End snapshot linked successfully")
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to link snapshot")
		}
	}

	// Handle resolve all
	// Uses batchExecute with MulticallerWithSender to preserve msg.sender for authorization
	const handleResolveAll = async () => {
		if (!walletClient) {
			toast.error("Wallet not connected. Please connect your wallet first.")
			return
		}
		if (!preview?.markets) {
			toast.error("No markets to resolve.")
			return
		}
		if (!preview.endSnapshot?.ipfsCid) {
			toast.error("End snapshot must be uploaded to IPFS first.")
			return
		}

		// Pre-flight: Check wallet is authorized operator or resolver (from on-chain registry)
		const walletAddress = walletClient.account?.address?.toLowerCase()
		if (!onChainOperator && !onChainResolver) {
			toast.error("Could not fetch operator/resolver from registry. Please try again.")
			return
		}
		const isOperator = onChainOperator && walletAddress === onChainOperator.toLowerCase()
		const isResolver = onChainResolver && walletAddress === onChainResolver.toLowerCase()
		if (!isOperator && !isResolver) {
			toast.error(
				`Wallet ${walletAddress?.slice(0, 10)}... is not an authorized operator or resolver.`,
			)
			return
		}

		setShowConfirmDialog(null)
		setIsResolvingAll(true)

		// Allow resolving unresolved markets AND re-resolving resolved (but not finalized) markets
		// This enables correcting resolutions with a different snapshot before finalization
		const resolvableMarkets = preview.markets.filter(m => {
			const state = onChainStates.get(m.marketAddress)
			return !state?.isFinalized // Can resolve/re-resolve if not finalized
		})

		if (resolvableMarkets.length === 0) {
			toast.info("All markets are already finalized")
			setIsResolvingAll(false)
			return
		}

		setTxDialog({
			open: true,
			status: "pending",
			hash: undefined,
			title: "Resolve Markets",
			description: `Resolving ${resolvableMarkets.length} markets...`,
			errorMessage: "",
			operationCount: resolvableMarkets.length,
		})

		try {
			const ipfsCid = preview.endSnapshot.ipfsCid

			const transactions = resolvableMarkets.map(market =>
				encodeResolve({
					market: getAddress(market.marketAddress),
					winningBucket: market.winningBucket,
					dataUri: ipfsUri(ipfsCid),
				}),
			)

			const { txHash } = await batchExecute(publicClient, walletClient, { transactions })
			setTxDialog(prev => ({ ...prev, hash: txHash, status: "confirming" }))

			const receipt = await publicClient.waitForTransactionReceipt({
				hash: txHash,
				timeout: 120_000,
			})

			if (receipt.status === "success") {
				setTxDialog(prev => ({ ...prev, status: "confirmed" }))
				toast.success(`Successfully resolved ${resolvableMarkets.length} markets`)

				if (adminPassword && preview?.endSnapshot) {
					await recordResolutionTxBatchMutation({
						adminPassword,
						resolutions: resolvableMarkets.map(market => ({
							marketAddress: market.marketAddress,
							txHash,
							winningBucket: market.winningBucket,
							dataUri: `ipfs://${ipfsCid}`,
						})),
						endSnapshotId: preview.endSnapshot.id,
					})
				}
			} else {
				const errorMsg = "Transaction reverted on-chain"
				setTxDialog(prev => ({ ...prev, status: "error", errorMessage: errorMsg }))
				toast.error(errorMsg)
			}
		} catch (error) {
			const errorMsg = error instanceof Error ? error.message : "Transaction failed"
			console.error("[Resolve Error]", error)
			setTxDialog(prev => ({ ...prev, status: "error", errorMessage: errorMsg }))
			toast.error(errorMsg)
		} finally {
			setIsResolvingAll(false)
			queryClient.invalidateQueries()
			fetchOnChainStates(preview.markets).catch(err =>
				console.error("Failed to refresh on-chain states:", err),
			)
		}
	}

	// Handle finalize all
	const handleFinalizeAll = async () => {
		if (!walletClient || !preview?.markets) {
			return
		}

		// Pre-flight: Check wallet is authorized resolver (from on-chain registry)
		const walletAddress = walletClient.account?.address?.toLowerCase()
		if (!onChainResolver) {
			toast.error("Could not fetch resolver from registry. Please try again.")
			return
		}
		if (walletAddress !== onChainResolver.toLowerCase()) {
			toast.error(
				`Wallet ${walletAddress?.slice(0, 10)}... is not the authorized resolver. Expected: ${onChainResolver.slice(0, 10)}...`,
			)
			return
		}

		setShowConfirmDialog(null)
		setIsFinalizingAll(true)

		setTxDialog({
			open: true,
			status: "pending",
			hash: undefined,
			title: "Finalize Markets",
			description: `Finalizing ${preview.markets.length} markets...`,
			errorMessage: "",
			operationCount: preview.markets.length,
		})

		try {
			// SDK normalizes addresses to lowercase, so use lowercase for lookups
			const addresses = preview.markets.map(m => getAddress(m.marketAddress))
			const { resolutions, errors } = await getResolutionsBatch(publicClient, {
				markets: addresses,
			})

			if (errors.size > 0) {
				throw new Error(`Failed to fetch state for some markets`)
			}

			const transactions = preview.markets
				.map(market => {
					const normalizedAddress = market.marketAddress.toLowerCase()
					const resolution = resolutions.get(normalizedAddress)
					if (!resolution?.isResolved || resolution.isFinalized) {
						return null
					}
					return encodeFinalize({
						market: getAddress(market.marketAddress),
						nonce: resolution.nonce,
					})
				})
				.filter((tx): tx is NonNullable<typeof tx> => tx !== null)

			if (transactions.length === 0) {
				setTxDialog(prev => ({ ...prev, status: "error", errorMessage: "No markets to finalize" }))
				return
			}

			const { txHash } = await batchExecute(publicClient, walletClient, { transactions })
			setTxDialog(prev => ({ ...prev, hash: txHash, status: "confirming" }))

			const receipt = await publicClient.waitForTransactionReceipt({
				hash: txHash,
				timeout: 120_000,
			})

			if (receipt.status === "success") {
				setTxDialog(prev => ({ ...prev, status: "confirmed" }))
				toast.success(`Successfully finalized ${transactions.length} markets`)

				// Record finalization TX hashes to DB and auto-update week status
				if (adminPassword) {
					const finalizedAddresses = preview.markets
						.filter(market => {
							const normalizedAddress = market.marketAddress.toLowerCase()
							const resolution = resolutions.get(normalizedAddress)
							return resolution?.isResolved && !resolution.isFinalized
						})
						.map(market => ({
							marketAddress: market.marketAddress,
							txHash,
						}))

					if (finalizedAddresses.length > 0) {
						await recordFinalizationTxBatchMutation({
							adminPassword,
							finalizations: finalizedAddresses,
						})
					}
				}
			} else {
				const errorMsg = "Transaction reverted on-chain"
				setTxDialog(prev => ({ ...prev, status: "error", errorMessage: errorMsg }))
				toast.error(errorMsg)
			}
		} catch (error) {
			const errorMsg = error instanceof Error ? error.message : "Transaction failed"
			console.error("[Finalize Error]", error)
			setTxDialog(prev => ({ ...prev, status: "error", errorMessage: errorMsg }))
			toast.error(errorMsg)
		} finally {
			setIsFinalizingAll(false)
			queryClient.invalidateQueries()
			fetchOnChainStates(preview.markets).catch(err =>
				console.error("Failed to refresh on-chain states:", err),
			)
		}
	}

	// Pre-flight checks
	const preflightChecks = useMemo(() => {
		if (!preview) {
			return []
		}

		const checks = [
			{
				label: "End snapshot captured",
				passed: !!preview.endSnapshot,
				message: preview.endSnapshot
					? `Captured ${formatDateTimeMs(preview.endSnapshot.timestampMs)}`
					: "Select an end snapshot",
			},
			{
				label: "End snapshot on IPFS",
				passed: !!preview.endSnapshot?.ipfsCid,
				message: preview.endSnapshot?.ipfsCid
					? `CID: ${preview.endSnapshot.ipfsCid?.slice(0, 12) ?? "N/A"}...`
					: "Upload to IPFS first",
			},
			{
				label: "All outcomes computed",
				passed: preview.isReady && preview.markets?.length > 0,
				message: preview.isReady
					? `${preview.markets?.length} markets ready`
					: preview.error || "Computing...",
			},
		]

		return checks
	}, [preview])

	const allChecksPassed = preflightChecks.every(c => c.passed)

	// Stats
	const stats = useMemo(() => {
		const states = [...onChainStates.values()]
		const pending = states.filter(s => !s.loading && !s.isResolved).length
		const resolved = states.filter(s => !s.loading && s.isResolved && !s.isFinalized).length
		return {
			total: preview?.markets?.length ?? 0,
			pending,
			resolved,
			finalized: states.filter(s => !s.loading && s.isFinalized).length,
			// Resolvable = can be resolved or re-resolved (not finalized)
			resolvable: pending + resolved,
		}
	}, [preview, onChainStates])

	// Check if any resolution would actually change (to prevent useless re-resolution)
	const hasResolutionChanges = useMemo(() => {
		if (!preview?.markets) {
			return false
		}

		// If any market is not yet resolved, we have changes to make
		for (const market of preview.markets) {
			const state = onChainStates.get(market.marketAddress)
			if (!state || state.loading) {
				continue
			}
			if (state.isFinalized) {
				continue // Skip finalized
			}

			// Not resolved yet = definitely has changes
			if (!state.isResolved) {
				return true
			}

			// Already resolved but bucket differs = has changes
			if (state.winningOutcome !== market.winningBucket) {
				return true
			}
		}

		return false
	}, [preview?.markets, onChainStates])

	if (previewLoading) {
		return (
			<div className="space-y-6">
				{/* Wizard steps skeleton */}
				<div className="flex items-center gap-2">
					{[1, 2, 3, 4].map(step => (
						<div className="flex items-center" key={step}>
							<Skeleton className="size-8 rounded-full" />
							{step < 4 && <Skeleton className="h-0.5 w-8" />}
						</div>
					))}
					<Skeleton className="ml-4 h-4 w-32" />
				</div>

				{/* Pre-flight checks skeleton */}
				<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
					<Skeleton className="mb-3 h-5 w-32" />
					<div className="space-y-2">
						{[0, 1, 2].map(i => (
							<div className="flex items-center gap-3" key={i}>
								<Skeleton className="size-5 rounded-full" />
								<div>
									<Skeleton className="mb-1 h-4 w-36" />
									<Skeleton className="h-3 w-48" />
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Stats skeleton */}
				<div className="grid gap-4 md:grid-cols-4">
					{[0, 1, 2, 3].map(i => (
						<div className="rounded-lg border border-gray-800 bg-gray-900 p-4" key={i}>
							<Skeleton className="mb-2 h-3 w-24" />
							<Skeleton className="h-8 w-12" />
						</div>
					))}
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			{/* Wizard Steps */}
			<div className="flex items-center gap-2">
				{[1, 2, 3, 4].map(step => (
					<div className="flex items-center" key={step}>
						<div
							className={cn(
								"flex size-8 items-center justify-center rounded-full font-medium text-sm",
								step < wizardStep
									? "bg-green-500 text-white"
									: step === wizardStep
										? "bg-gray-500 text-white"
										: "bg-gray-700 text-gray-400",
							)}
						>
							{step < wizardStep ? <CheckCircle aria-hidden="true" className="size-5" /> : step}
						</div>
						{step < 4 && (
							<div
								className={cn("h-0.5 w-8", step < wizardStep ? "bg-green-500" : "bg-gray-700")}
							/>
						)}
					</div>
				))}
				<div className="ml-4 text-gray-400 text-sm">
					{wizardStep === 1 && "Select End Snapshot"}
					{wizardStep === 2 && "Review & Resolve"}
					{wizardStep === 3 && "Finalize Markets"}
					{wizardStep === 4 && "Complete"}
				</div>
			</div>

			{/* Snapshot Status Indicator */}
			<div
				className={cn(
					"flex items-center gap-3 rounded-lg border p-4",
					preview?.endSnapshot
						? "border-green-500/30 bg-green-500/5"
						: "border-gray-700 bg-gray-900",
				)}
			>
				{preview?.endSnapshot ? (
					<CheckCircle2 aria-hidden="true" className="size-5 shrink-0 text-green-400" />
				) : (
					<div className="size-5 shrink-0 rounded border-2 border-gray-600" />
				)}
				<div className="flex-1">
					<div
						className={cn(
							"font-medium text-sm",
							preview?.endSnapshot ? "text-green-400" : "text-gray-400",
						)}
					>
						{preview?.endSnapshot ? "End Snapshot Taken" : "End Snapshot Not Taken"}
					</div>
					{preview?.endSnapshot ? (
						<div className="text-gray-500 text-xs">
							Captured {formatDateTimeMs(preview.endSnapshot.timestampMs)}
							{preview.endSnapshot.ipfsCid ? " · On IPFS" : " · Pending IPFS upload"}
						</div>
					) : (
						<div className="text-gray-500 text-xs">
							Select an end snapshot below to proceed with resolution
						</div>
					)}
				</div>
			</div>

			{/* Pre-flight Checks */}
			{wizardStep <= 2 && (
				<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
					<h3 className="mb-3 font-medium">Pre-flight Checks</h3>
					<div className="space-y-2">
						{preflightChecks.map(check => (
							<div className="flex items-center gap-3" key={check.label}>
								{check.passed ? (
									<CheckCircle aria-hidden="true" className="size-5 text-green-400" />
								) : (
									<XCircle aria-hidden="true" className="size-5 text-gray-500" />
								)}
								<div>
									<div className={check.passed ? "text-green-400" : "text-gray-400"}>
										{check.label}
									</div>
									<div className="text-gray-500 text-xs">{check.message}</div>
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Change Snapshot Button (when already on step 2+) */}
			{wizardStep >= 2 && !showSnapshotSelector && preview?.endSnapshot && (
				<div className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-900 p-4">
					<div>
						<div className="text-gray-500 text-sm">Current End Snapshot</div>
						<div className="text-gray-300 text-sm">
							{formatDateTimeMs(preview.endSnapshot.timestampMs)}
						</div>
					</div>
					<button
						className="rounded bg-gray-700 px-3 py-1.5 text-gray-300 text-sm hover:bg-gray-600"
						onClick={() => setShowSnapshotSelector(true)}
						type="button"
					>
						Change Snapshot
					</button>
				</div>
			)}

			{/* Step 1: Select End Snapshot (or change snapshot) */}
			{(wizardStep === 1 || showSnapshotSelector) && (
				<div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4">
					<div className="mb-2 flex items-center justify-between">
						<h3 className="font-medium text-yellow-400">
							{preview?.endSnapshot ? "Change End Snapshot" : "Select End Snapshot"}
						</h3>
						{showSnapshotSelector && preview?.endSnapshot && (
							<button
								className="text-gray-400 text-sm hover:text-white"
								onClick={() => setShowSnapshotSelector(false)}
								type="button"
							>
								Cancel
							</button>
						)}
					</div>
					<p className="mb-4 text-gray-400 text-sm">
						Choose the snapshot to use for resolution calculations.
						{preview?.endSnapshot && " Selecting a new snapshot will update the resolution data."}
					</p>
					<div className="flex flex-wrap gap-2">
						{snapshots?.map(snapshot => (
							<button
								className={`rounded-lg px-3 py-2 text-sm ${
									preview?.endSnapshot?.id === snapshot.id
										? "border-2 border-green-500 bg-green-900/30"
										: "bg-gray-800 hover:bg-gray-700"
								}`}
								key={snapshot.id}
								onClick={() => {
									handlePrepareResolution(snapshot.id)
										.then(() => setShowSnapshotSelector(false))
										.catch(err => toast.error(err.message))
								}}
								type="button"
							>
								<div className="text-xs">
									{formatDateTimeMs(snapshot.capturedAtMs)} ({snapshot.entityCount} entities)
								</div>
								<div className="text-gray-500 text-xs">
									{snapshot.ipfsCid ? (
										<span className="text-green-500">On IPFS</span>
									) : (
										<span className="text-yellow-500">Pending IPFS</span>
									)}
								</div>
							</button>
						))}
					</div>
				</div>
			)}

			{/* Step 2-3: Resolution Stats & Actions */}
			{preview?.isReady && wizardStep >= 2 && (
				<>
					{/* Stats */}
					<div className="grid gap-4 md:grid-cols-4">
						<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
							<div className="text-gray-500 text-xs uppercase">Total Markets</div>
							<div className="mt-1 font-bold text-2xl">{stats.total}</div>
						</div>
						<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
							<div className="text-gray-500 text-xs uppercase">Pending Resolution</div>
							<div className="mt-1 font-bold text-2xl text-yellow-400">{stats.pending}</div>
						</div>
						<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
							<div className="text-gray-500 text-xs uppercase">Resolved (Awaiting Finalize)</div>
							<div className="mt-1 font-bold text-2xl text-blue-400">{stats.resolved}</div>
						</div>
						<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
							<div className="text-gray-500 text-xs uppercase">Finalized</div>
							<div className="mt-1 font-bold text-2xl text-green-400">{stats.finalized}</div>
						</div>
					</div>

					{/* Actions */}
					{isHotWalletMode && (
						<div className="flex items-center gap-4">
							{stats.resolvable > 0 && (
								<>
									<button
										className="rounded-lg bg-gray-600 px-4 py-2 font-medium text-sm text-white hover:bg-gray-500 disabled:opacity-50"
										disabled={
											isResolvingAll || isFinalizingAll || !allChecksPassed || !hasResolutionChanges
										}
										onClick={() => setShowConfirmDialog("resolve")}
										type="button"
									>
										{isResolvingAll
											? "Resolving..."
											: stats.pending > 0
												? `Resolve All (${stats.resolvable})`
												: `Re-resolve All (${stats.resolvable})`}
									</button>
									{!hasResolutionChanges && stats.pending === 0 && (
										<span className="text-gray-500 text-sm">
											All resolutions match on-chain state
										</span>
									)}
								</>
							)}
							{stats.resolved > 0 && (
								<button
									className="rounded-lg bg-gray-600 px-4 py-2 font-medium text-sm text-white hover:bg-gray-500 disabled:opacity-50"
									disabled={isResolvingAll || isFinalizingAll}
									onClick={() => setShowConfirmDialog("finalize")}
									type="button"
								>
									{isFinalizingAll ? "Finalizing..." : `Finalize All (${stats.resolved})`}
								</button>
							)}
							<button
								aria-label="Refresh on-chain state"
								className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-gray-300 text-sm hover:bg-gray-700 disabled:opacity-50"
								disabled={isLoadingOnChainState}
								onClick={() =>
									fetchOnChainStates(preview.markets).catch(err => toast.error(err.message))
								}
								type="button"
							>
								<RefreshCw
									aria-hidden="true"
									className={cn("size-4", isLoadingOnChainState && "animate-spin")}
								/>
								Refresh
							</button>
						</div>
					)}

					{/* Safe Batch Panel */}
					{isSafeMode && (
						<SafeBatchPanel
							chainId={chainId}
							preview={preview}
							publicClient={publicClient}
							weekId={weekId}
						/>
					)}

					{/* Markets Table */}
					<div className="rounded-lg border border-gray-800 bg-gray-900">
						<div className="border-gray-800 border-b px-4 py-3">
							<h2 className="font-medium">Resolution Preview ({preview.markets.length} markets)</h2>
						</div>
						<div className="overflow-x-auto">
							<table className="w-full text-left text-sm">
								<thead>
									<tr className="border-gray-800 border-b text-gray-500">
										<th className="px-4 py-3">Market</th>
										<th className="px-4 py-3 text-center">Start</th>
										<th className="px-4 py-3 text-center">End</th>
										<th className="px-4 py-3 text-center">Δ</th>
										<th className="px-4 py-3">Computed Winner</th>
										<th className="px-4 py-3">On-Chain</th>
										<th className="px-4 py-3">Status</th>
									</tr>
								</thead>
								<tbody>
									{preview.markets.map(market => {
										const state = onChainStates.get(market.marketAddress)
										const isMismatch =
											state?.isResolved && state.winningOutcome !== market.winningBucket

										return (
											<tr
												className={cn(
													"border-gray-800 border-b",
													isMismatch ? "bg-red-500/10" : "hover:bg-gray-800/50",
												)}
												key={market.marketAddress}
											>
												<td className="px-4 py-3">
													<div className="font-medium">{market.displayName}</div>
													<div className="font-mono text-gray-500 text-xs">
														{truncateAddress(market.marketAddress, 6)}
													</div>
												</td>
												<td className="px-4 py-3 text-center font-mono text-gray-400">
													{market.startRank}
												</td>
												<td className="px-4 py-3 text-center font-mono text-gray-400">
													{market.endRank}
												</td>
												<td className="px-4 py-3 text-center">
													<span
														className={
															market.rankDelta > 0
																? "text-green-400"
																: market.rankDelta < 0
																	? "text-red-400"
																	: "text-gray-400"
														}
													>
														{market.rankDelta > 0 ? "+" : ""}
														{market.rankDelta}
													</span>
												</td>
												<td className="px-4 py-3">
													<span
														className={`rounded px-2 py-1 text-xs ${
															market.winningBucket === 0
																? "bg-green-500/20 text-green-400"
																: "bg-red-500/20 text-red-400"
														}`}
													>
														{market.winningOutcome}
													</span>
												</td>
												<td className="px-4 py-3">
													{state?.loading ? (
														<span className="text-gray-500 text-xs">Loading...</span>
													) : state?.isResolved ? (
														<span className={isMismatch ? "text-red-400" : "text-gray-400"}>
															Bucket {state.winningOutcome}
															{isMismatch && " (MISMATCH)"}
														</span>
													) : (
														<span className="text-gray-500 text-xs">Not resolved</span>
													)}
												</td>
												<td className="px-4 py-3">
													{state?.isFinalized ? (
														<span className="rounded bg-green-500/20 px-2 py-0.5 text-green-400 text-xs">
															Finalized
														</span>
													) : state?.isResolved ? (
														<span className="rounded bg-blue-500/20 px-2 py-0.5 text-blue-400 text-xs">
															Resolved
														</span>
													) : (
														<span className="rounded bg-yellow-500/20 px-2 py-0.5 text-xs text-yellow-400">
															Pending
														</span>
													)}
												</td>
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>
					</div>
				</>
			)}

			{/* Step 4: Complete */}
			{wizardStep === 4 && (
				<div className="rounded-lg border border-green-500/30 bg-green-500/5 p-6 text-center">
					<CheckCircle aria-hidden="true" className="mx-auto mb-2 size-12 text-green-400" />
					<h3 className="font-semibold text-green-400 text-lg">Resolution Complete!</h3>
					<p className="mt-2 text-gray-400">
						All markets have been finalized. Claims are now enabled.
					</p>
				</div>
			)}

			{/* Confirmation Dialogs */}
			{/* Resolve Confirmation Dialog */}
			{showConfirmDialog === "resolve" && (
				// biome-ignore lint/a11y/noStaticElementInteractions: modal backdrop pattern
				<div
					className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-black/50"
					onClick={e => {
						if (e.target === e.currentTarget) {
							setShowConfirmDialog(null)
						}
					}}
					onKeyDown={e => {
						if (e.key === "Escape") {
							setShowConfirmDialog(null)
						}
					}}
					role="presentation"
				>
					{/* biome-ignore lint/a11y/noStaticElementInteractions: modal content container */}
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation for modal content */}
					<div className="rounded-xl bg-gray-800 p-6 shadow-xl" onClick={e => e.stopPropagation()}>
						<h3 className="mb-2 font-semibold text-lg text-white">
							{stats.pending > 0 ? "Resolve All Markets?" : "Re-resolve All Markets?"}
						</h3>
						<p className="mb-6 text-gray-400 text-sm">
							{stats.pending > 0
								? `This will submit a batch transaction to resolve ${stats.resolvable} markets. Markets can be re-resolved before finalization if needed.`
								: `This will re-resolve ${stats.resolvable} markets with the current end snapshot. Use this to correct resolutions before finalization.`}
						</p>
						<div className="flex justify-end gap-3">
							<button
								className="rounded-lg border border-gray-600 px-4 py-2 text-gray-300 hover:bg-gray-700"
								onClick={() => setShowConfirmDialog(null)}
								type="button"
							>
								Cancel
							</button>
							<button
								className="rounded-lg bg-gray-600 px-4 py-2 text-white hover:bg-gray-500"
								onClick={() =>
									handleResolveAll().catch(err => toast.error(err?.message || String(err)))
								}
								type="button"
							>
								Resolve All
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Finalize Confirmation Dialog */}
			{showConfirmDialog === "finalize" && (
				// biome-ignore lint/a11y/noStaticElementInteractions: modal backdrop pattern
				<div
					className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-black/50"
					onClick={e => {
						if (e.target === e.currentTarget) {
							setShowConfirmDialog(null)
						}
					}}
					onKeyDown={e => {
						if (e.key === "Escape") {
							setShowConfirmDialog(null)
						}
					}}
					role="presentation"
				>
					{/* biome-ignore lint/a11y/noStaticElementInteractions: modal content container */}
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation for modal content */}
					<div className="rounded-xl bg-gray-800 p-6 shadow-xl" onClick={e => e.stopPropagation()}>
						<h3 className="mb-2 font-semibold text-lg text-white">Finalize All Markets?</h3>
						<p className="mb-6 text-gray-400 text-sm">
							This will submit a batch transaction to finalize {stats.resolved} markets. Once
							finalized, resolutions are locked and claims are enabled. This cannot be undone.
						</p>
						<div className="flex justify-end gap-3">
							<button
								className="rounded-lg border border-gray-600 px-4 py-2 text-gray-300 hover:bg-gray-700"
								onClick={() => setShowConfirmDialog(null)}
								type="button"
							>
								Cancel
							</button>
							<button
								className="rounded-lg bg-gray-600 px-4 py-2 text-white hover:bg-gray-500"
								onClick={() =>
									handleFinalizeAll().catch(err => toast.error(err?.message || String(err)))
								}
								type="button"
							>
								Finalize All
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Transaction Status Dialog */}
			<TransactionStatusDialog
				chainId={chainId}
				description={txDialog.description}
				errorMessage={txDialog.errorMessage}
				onOpenChange={open => setTxDialog(prev => ({ ...prev, open }))}
				open={txDialog.open}
				operationCount={txDialog.operationCount}
				status={txDialog.status}
				title={txDialog.title}
				txHash={txDialog.hash}
			/>
		</div>
	)
}

// ============================================================================
// Fees Tab
// ============================================================================

function FeesTab({ weekId }: { weekId: string }) {
	const { isConnected } = useAccount()
	const { data: walletClient } = useWalletClient()
	const { publicClient, chainId } = useAdminConfig()
	const { treasury: onChainTreasury, isLoading: registryLoading } = useRegistryConfig()
	const { isHotWalletMode, isSafeMode } = useExecutionMode()
	const queryClient = useQueryClient()

	// Fee sweep state
	const [selectedMarkets, setSelectedMarkets] = useState<Set<string>>(new Set())
	const [executingMarkets, setExecutingMarkets] = useState<Set<string>>(new Set())
	const [txResults, setTxResults] = useState<
		Map<string, { hash: string; status: "success" | "error" }>
	>(new Map())
	const [isSweepingAll, setIsSweepingAll] = useState(false)
	const [txDialog, setTxDialog] = useState<TxDialogState>(initialTxDialogState)

	// Reserve sweep state
	const [reserveTxResults, setReserveTxResults] = useState<
		Map<string, { hash: string; status: "success" | "error" }>
	>(new Map())
	const [isSweepingAllReserves, setIsSweepingAllReserves] = useState(false)

	// Fetch markets
	const { data: markets } = useQuery({
		...convexQuery(api.domains.markets.queries.list, { weekId }),
		enabled: isConnected,
	})

	// Fetch fee data (RPC-based, needs cache config)
	const { data: feeData, isLoading: feeDataLoading } = useQuery({
		queryKey: ["admin", "fees", weekId, markets?.map(m => m.marketAddress)],
		queryFn: async (): Promise<MarketFeeData[]> => {
			if (!markets) {
				return []
			}
			return Promise.all(
				markets.map(async (market): Promise<MarketFeeData> => {
					const displayName = market.displayName || market.handle || market.marketAddress
					try {
						const fees = await getFees(publicClient, { market: getAddress(market.marketAddress) })
						return { marketAddress: market.marketAddress, displayName, fees }
					} catch {
						return { marketAddress: market.marketAddress, displayName, fees: null }
					}
				}),
			)
		},
		enabled: !!markets && markets.length > 0,
		staleTime: 30_000, // 30s - fees don't change frequently
		refetchInterval: 60_000, // Refresh every minute
	})

	// Fetch reserve data for finalized markets (RPC-based, uses multicall for reliability)
	const { data: reserveData, isLoading: reserveDataLoading } = useQuery({
		queryKey: ["admin", "reserves", weekId, markets?.map(m => m.marketAddress)],
		queryFn: async (): Promise<MarketReserveData[]> => {
			if (!markets) {
				return []
			}

			const addresses = markets.map(m => getAddress(m.marketAddress))

			// Batch fetch resolution status + balances in parallel via multicall
			const [{ resolutions }, { balances, errors: balanceErrors }] = await Promise.all([
				getResolutionsBatch(publicClient, { markets: addresses }),
				getMarketBalancesBatch(publicClient, { markets: addresses }),
			])

			return markets.map((market): MarketReserveData => {
				const displayName = market.displayName || market.handle || market.marketAddress
				const normalizedAddress = market.marketAddress.toLowerCase()
				const resolution = resolutions.get(normalizedAddress)
				const isFinalized = resolution?.isFinalized ?? false
				const balanceInfo = balances.get(normalizedAddress)
				const balanceError = balanceErrors.get(normalizedAddress)

				if (balanceError) {
					console.error(
						`[reserves] Failed to read balance for ${market.marketAddress}:`,
						balanceError,
					)
				}

				return {
					marketAddress: market.marketAddress,
					displayName,
					isFinalized,
					balance: balanceInfo?.balance ?? null,
				}
			})
		},
		enabled: !!markets && markets.length > 0,
		staleTime: 30_000,
		refetchInterval: 60_000,
	})

	// Handle sweep all
	const handleSweepAllFees = async () => {
		if (!walletClient || !feeData) {
			return
		}

		const marketsToSweep = feeData.filter(
			m => m.fees && m.fees.protocol > 0 && !txResults.get(`fees-${m.marketAddress}`),
		)

		if (marketsToSweep.length === 0) {
			return
		}

		setIsSweepingAll(true)

		setTxDialog({
			open: true,
			status: "pending",
			hash: undefined,
			title: "Sweep All Fees",
			description: `Sweeping fees from ${marketsToSweep.length} markets...`,
			errorMessage: "",
			operationCount: marketsToSweep.length,
		})

		try {
			const transactions = marketsToSweep.map(market =>
				encodeSweepProtocolFees({ market: getAddress(market.marketAddress) }),
			)

			const { txHash } = await batchExecute(publicClient, walletClient, { transactions })
			setTxDialog(prev => ({ ...prev, hash: txHash, status: "confirming" }))

			const receipt = await publicClient.waitForTransactionReceipt({
				hash: txHash,
				timeout: 120_000,
			})

			if (receipt.status === "success") {
				setTxDialog(prev => ({ ...prev, status: "confirmed" }))
				setTxResults(prev => {
					const next = new Map(prev)
					for (const m of marketsToSweep) {
						next.set(`fees-${m.marketAddress}`, { hash: txHash, status: "success" })
					}
					return next
				})
			} else {
				setTxDialog(prev => ({ ...prev, status: "error", errorMessage: "Transaction reverted" }))
			}
		} catch (error) {
			setTxDialog(prev => ({
				...prev,
				status: "error",
				errorMessage: error instanceof Error ? error.message : "Transaction failed",
			}))
		} finally {
			setIsSweepingAll(false)
			queryClient.invalidateQueries({ queryKey: ["admin", "fees"] })
		}
	}

	// Generate Safe batch
	const generateSweepFeesBatch = () => {
		if (!feeData || selectedMarkets.size === 0) {
			return null
		}

		const transactions: SafeTransaction[] = Array.from(selectedMarkets).map(marketAddress =>
			encodeSweepProtocolFees({ market: getAddress(marketAddress) }),
		)

		const batch = toSafeTransactionBatch({
			name: `Sweep Fees - ${selectedMarkets.size} markets`,
			transactions,
			chainId,
		})

		return serializeSafeBatch(batch)
	}

	// Handle sweep all reserves - sweeps ALL finalized markets
	// sweepReserve() is safe: if there's nothing to sweep, it just returns (no revert)
	const handleSweepAllReserves = async () => {
		if (!walletClient || !reserveData) {
			return
		}

		const marketsToSweep = reserveData.filter(
			m => m.isFinalized && !reserveTxResults.get(`reserve-${m.marketAddress}`),
		)

		if (marketsToSweep.length === 0) {
			return
		}

		setIsSweepingAllReserves(true)

		setTxDialog({
			open: true,
			status: "pending",
			hash: undefined,
			title: "Sweep All Reserves",
			description: `Sweeping reserves from ${marketsToSweep.length} finalized markets...`,
			errorMessage: "",
			operationCount: marketsToSweep.length,
		})

		try {
			const transactions = marketsToSweep.map(market =>
				encodeSweepReserve({ market: getAddress(market.marketAddress) }),
			)

			const { txHash } = await batchExecute(publicClient, walletClient, { transactions })
			setTxDialog(prev => ({ ...prev, hash: txHash, status: "confirming" }))

			const receipt = await publicClient.waitForTransactionReceipt({
				hash: txHash,
				timeout: 120_000,
			})

			if (receipt.status === "success") {
				setTxDialog(prev => ({ ...prev, status: "confirmed" }))
				setReserveTxResults(prev => {
					const next = new Map(prev)
					for (const m of marketsToSweep) {
						next.set(`reserve-${m.marketAddress}`, { hash: txHash, status: "success" })
					}
					return next
				})
			} else {
				setTxDialog(prev => ({ ...prev, status: "error", errorMessage: "Transaction reverted" }))
			}
		} catch (error) {
			setTxDialog(prev => ({
				...prev,
				status: "error",
				errorMessage: error instanceof Error ? error.message : "Transaction failed",
			}))
		} finally {
			setIsSweepingAllReserves(false)
			queryClient.invalidateQueries({ queryKey: ["admin", "reserves"] })
		}
	}

	// Generate Safe batch for reserve sweep (all finalized markets)
	// sweepReserve() is safe: if there's nothing to sweep, it just returns (no revert)
	const generateSweepReservesBatch = () => {
		if (!reserveData) {
			return null
		}
		const marketsToSweep = reserveData.filter(m => m.isFinalized)
		if (marketsToSweep.length === 0) {
			return null
		}

		const transactions: SafeTransaction[] = marketsToSweep.map(m =>
			encodeSweepReserve({ market: getAddress(m.marketAddress) }),
		)

		const batch = toSafeTransactionBatch({
			name: `Sweep Reserves - ${marketsToSweep.length} markets`,
			transactions,
			chainId,
		})

		return serializeSafeBatch(batch)
	}

	const totalProtocolFees = feeData?.reduce((acc, m) => acc + (m.fees?.protocol ?? 0), 0) ?? 0
	const marketsWithFees = feeData?.filter(m => m.fees && m.fees.protocol > 0).length ?? 0

	const finalizedMarkets = reserveData?.filter(m => m.isFinalized) ?? []
	const allMarketsWithBalance = reserveData?.filter(m => m.balance !== null) ?? []
	const marketsWithReadErrors = reserveData?.filter(m => m.balance === null) ?? []
	const totalBalance = allMarketsWithBalance.reduce((acc, m) => acc + (m.balance ?? 0), 0)

	return (
		<div className="space-y-6">
			{/* Summary */}
			<div className="grid gap-4 sm:grid-cols-2">
				<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
					<div className="mb-1 text-gray-500 text-sm">Total Protocol Fees</div>
					<div className="font-semibold text-green-400 text-xl">
						{formatUsdc(totalProtocolFees)}
					</div>
				</div>
				<div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
					<div className="mb-1 text-gray-500 text-sm">Markets with Fees</div>
					<div className="font-semibold text-xl">
						{marketsWithFees} / {feeData?.length ?? 0}
					</div>
				</div>
			</div>

			{/* Treasury Info (from on-chain registry) */}
			<div className="rounded-lg border border-gray-800 bg-gray-900 px-4 py-3">
				<span className="text-gray-500 text-sm">Treasury:</span>
				{registryLoading ? (
					<span className="ml-2 inline-block h-4 w-32 animate-pulse rounded bg-gray-700" />
				) : onChainTreasury ? (
					<code className="ml-2 font-mono text-gray-400 text-xs">{onChainTreasury}</code>
				) : (
					<span className="ml-2 text-gray-600 text-xs">-</span>
				)}
			</div>

			{/* Actions */}
			{isHotWalletMode && feeData && marketsWithFees > 0 && (
				<div className="flex items-center gap-4">
					<button
						className="rounded-lg bg-green-600 px-4 py-2 font-medium text-sm text-white hover:bg-green-500 disabled:opacity-50"
						disabled={isSweepingAll || marketsWithFees === 0}
						onClick={() => handleSweepAllFees().catch(err => toast.error(err.message))}
						type="button"
					>
						{isSweepingAll ? "Sweeping..." : `Sweep All Fees (${marketsWithFees} markets)`}
					</button>
				</div>
			)}

			{/* Safe Batch Actions */}
			{isSafeMode && selectedMarkets.size > 0 && (
				<div className="flex items-center gap-4 rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
					<span className="text-blue-400 text-sm">{selectedMarkets.size} markets selected</span>
					<button
						className="rounded bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-500"
						onClick={() => {
							const json = generateSweepFeesBatch()
							if (json) {
								navigator.clipboard.writeText(json)
								toast.success("Sweep fees batch copied to clipboard!")
							}
						}}
						type="button"
					>
						Copy Sweep Fees Batch
					</button>
				</div>
			)}

			{/* Markets Table */}
			<div className="rounded-lg border border-gray-800 bg-gray-900">
				<div className="flex items-center justify-between border-gray-800 border-b px-4 py-3">
					<h2 className="font-medium">Markets</h2>
					{isSafeMode && (
						<button
							className="text-gray-400 text-sm hover:text-gray-300"
							onClick={() =>
								feeData && setSelectedMarkets(new Set(feeData.map(m => m.marketAddress)))
							}
							type="button"
						>
							Select All
						</button>
					)}
				</div>
				<div className="overflow-x-auto">
					{feeDataLoading ? (
						<table className="w-full text-left text-sm">
							<thead>
								<tr className="border-gray-800 border-b text-gray-500">
									<th className="px-4 py-3">Market</th>
									<th className="px-4 py-3">Protocol Fees</th>
									<th className="px-4 py-3">Actions</th>
								</tr>
							</thead>
							<tbody>
								<TableSkeleton columns={3} rows={10} />
							</tbody>
						</table>
					) : (
						<table className="w-full text-left text-sm">
							<thead>
								<tr className="border-gray-800 border-b text-gray-500">
									{isSafeMode && (
										<th className="w-10 px-4 py-3">
											<span className="sr-only">Selection</span>
										</th>
									)}
									<th className="px-4 py-3">Market</th>
									<th className="px-4 py-3">Protocol Fees</th>
									{isHotWalletMode && <th className="px-4 py-3">Actions</th>}
								</tr>
							</thead>
							<tbody>
								{feeData?.map(market => {
									const feeResult = txResults.get(`fees-${market.marketAddress}`)
									const isExecuting = executingMarkets.has(market.marketAddress)

									return (
										<tr
											className="border-gray-800 border-b hover:bg-gray-800/50"
											key={market.marketAddress}
										>
											{isSafeMode && (
												<td className="px-4 py-3">
													<input
														aria-label={`Select ${market.displayName} for batch fee sweep`}
														checked={selectedMarkets.has(market.marketAddress)}
														className="size-4 rounded border-gray-600 bg-gray-700"
														onChange={() => {
															const next = new Set(selectedMarkets)
															if (next.has(market.marketAddress)) {
																next.delete(market.marketAddress)
															} else {
																next.add(market.marketAddress)
															}
															setSelectedMarkets(next)
														}}
														type="checkbox"
													/>
												</td>
											)}
											<td className="px-4 py-3">
												<div className="font-medium">{market.displayName}</div>
												<div className="font-mono text-gray-500 text-xs">
													{truncateAddress(market.marketAddress, 6)}
												</div>
											</td>
											<td className="px-4 py-3">
												{market.fees ? (
													<span className="text-green-400">{formatUsdc(market.fees.protocol)}</span>
												) : (
													<span className="text-gray-600">-</span>
												)}
											</td>
											{isHotWalletMode && (
												<td className="px-4 py-3">
													{feeResult?.status === "success" ? (
														<a
															className="text-green-400 text-xs"
															href={getExplorerTxUrl(chainId, feeResult.hash)}
															rel="noopener noreferrer"
															target="_blank"
														>
															Swept
														</a>
													) : (
														<button
															className="rounded bg-green-600 px-2 py-1 text-white text-xs hover:bg-green-500 disabled:opacity-50"
															disabled={isExecuting || !market.fees || market.fees.protocol === 0}
															onClick={async () => {
																if (!walletClient) {
																	return
																}
																setExecutingMarkets(prev => new Set(prev).add(market.marketAddress))
																try {
																	const hash = await sweepProtocolFees(walletClient, {
																		market: getAddress(market.marketAddress),
																	})
																	setTxResults(prev =>
																		new Map(prev).set(`fees-${market.marketAddress}`, {
																			hash,
																			status: "success",
																		}),
																	)
																} catch {
																	setTxResults(prev =>
																		new Map(prev).set(`fees-${market.marketAddress}`, {
																			hash: "",
																			status: "error",
																		}),
																	)
																} finally {
																	setExecutingMarkets(prev => {
																		const next = new Set(prev)
																		next.delete(market.marketAddress)
																		return next
																	})
																}
															}}
															type="button"
														>
															{isExecuting ? "..." : "Sweep"}
														</button>
													)}
												</td>
											)}
										</tr>
									)
								})}
							</tbody>
						</table>
					)}
				</div>
			</div>

			{/* Reserve Sweep Section */}
			<div className="rounded-lg border border-gray-800 bg-gray-900">
				<div className="border-gray-800 border-b px-4 py-3">
					<h2 className="font-medium">Reserve Sweep</h2>
					<p className="mt-1 text-gray-500 text-sm">
						Sweep remaining USDC from finalized markets to treasury
					</p>
				</div>

				{/* Reserve Summary */}
				<div className="grid gap-4 border-gray-800 border-b p-4 sm:grid-cols-3">
					<div>
						<div className="mb-1 text-gray-500 text-sm">Total USDC in Markets</div>
						{reserveDataLoading ? (
							<div className="h-7 w-20 animate-pulse rounded bg-gray-700" />
						) : (
							<div>
								<div className="font-semibold text-blue-400 text-lg">
									{formatUsdc(totalBalance)}
								</div>
								{marketsWithReadErrors.length > 0 && (
									<div className="mt-0.5 text-xs text-yellow-500">
										{marketsWithReadErrors.length} market
										{marketsWithReadErrors.length > 1 ? "s" : ""} failed to read
									</div>
								)}
							</div>
						)}
					</div>
					<div>
						<div className="mb-1 text-gray-500 text-sm">Finalized Markets</div>
						{reserveDataLoading ? (
							<div className="h-7 w-16 animate-pulse rounded bg-gray-700" />
						) : (
							<div className="font-semibold text-lg">
								{finalizedMarkets.length} / {reserveData?.length ?? 0}
							</div>
						)}
					</div>
					<div>
						<div className="mb-1 text-gray-500 text-sm">Balances Read</div>
						{reserveDataLoading ? (
							<div className="h-7 w-8 animate-pulse rounded bg-gray-700" />
						) : (
							<div className="font-semibold text-lg">
								{allMarketsWithBalance.length} / {reserveData?.length ?? 0}
							</div>
						)}
					</div>
				</div>

				{/* Reserve Actions */}
				<div className="px-4 py-3">
					{isHotWalletMode && (
						<button
							className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-sm text-white hover:bg-blue-500 disabled:opacity-50"
							disabled={
								isSweepingAllReserves || finalizedMarkets.length === 0 || reserveDataLoading
							}
							onClick={() => handleSweepAllReserves().catch(err => toast.error(err.message))}
							type="button"
						>
							{isSweepingAllReserves
								? "Sweeping..."
								: `Sweep All Reserves (${finalizedMarkets.length} markets)`}
						</button>
					)}
					{isSafeMode && (
						<button
							className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-sm text-white hover:bg-blue-500 disabled:opacity-50"
							disabled={finalizedMarkets.length === 0 || reserveDataLoading}
							onClick={() => {
								const json = generateSweepReservesBatch()
								if (json) {
									navigator.clipboard.writeText(json)
									toast.success("Sweep reserves batch copied to clipboard!")
								}
							}}
							type="button"
						>
							Copy Sweep Reserves Batch ({finalizedMarkets.length} markets)
						</button>
					)}
				</div>
			</div>

			{/* Transaction Status Dialog */}
			<TransactionStatusDialog
				chainId={chainId}
				description={txDialog.description}
				errorMessage={txDialog.errorMessage}
				onOpenChange={open => setTxDialog(prev => ({ ...prev, open }))}
				open={txDialog.open}
				operationCount={txDialog.operationCount}
				status={txDialog.status}
				title={txDialog.title}
				txHash={txDialog.hash}
			/>
		</div>
	)
}

// ============================================================================
// Safe Batch Panel (for Resolution Tab)
// ============================================================================

function SafeBatchPanel({
	preview,
	chainId,
	publicClient,
	weekId,
}: {
	preview: ResolutionPreview
	chainId: number
	publicClient: ReturnType<typeof useAdminConfig>["publicClient"]
	weekId: string
}) {
	const [activeStep, setActiveStep] = useState<"resolve" | "finalize" | null>(null)
	const [batchJson, setBatchJson] = useState<string | null>(null)
	const [isGenerating, setIsGenerating] = useState(false)
	const [copySuccess, setCopySuccess] = useState(false)
	const [nonceOverride, setNonceOverride] = useState<number | null>(1)

	const generateResolveBatch = useCallback(() => {
		if (!preview?.markets || !preview.endSnapshot?.ipfsCid) {
			return null
		}

		const dataUri = ipfsUri(preview.endSnapshot.ipfsCid)
		const transactions: SafeTransaction[] = preview.markets.map(m =>
			encodeResolve({
				market: getAddress(m.marketAddress),
				winningBucket: m.winningBucket,
				dataUri,
			}),
		)

		return serializeSafeBatch(
			toSafeTransactionBatch({
				name: `Resolve Week ${weekId.slice(0, 8)}`,
				transactions,
				chainId,
			}),
		)
	}, [preview, chainId, weekId])

	const generateFinalizeBatch = useCallback(async () => {
		if (!preview?.markets) {
			return null
		}

		// SDK normalizes addresses to lowercase, so use lowercase for lookups
		const addresses = preview.markets.map(m => getAddress(m.marketAddress))
		const { resolutions } = await getResolutionsBatch(publicClient, {
			markets: addresses,
		})

		const transactions: SafeTransaction[] = preview.markets
			.filter(m => {
				const normalizedAddress = m.marketAddress.toLowerCase()
				return resolutions.get(normalizedAddress)?.isResolved
			})
			.map(m => {
				const normalizedAddress = m.marketAddress.toLowerCase()
				const nonce = nonceOverride ?? resolutions.get(normalizedAddress)?.nonce ?? 1
				return encodeFinalize({
					market: getAddress(m.marketAddress),
					nonce,
				})
			})

		if (transactions.length === 0) {
			return null
		}

		return serializeSafeBatch(
			toSafeTransactionBatch({
				name: `Finalize Week ${weekId.slice(0, 8)} (nonce ${nonceOverride ?? "current"})`,
				transactions,
				chainId,
			}),
		)
	}, [preview, chainId, weekId, publicClient, nonceOverride])

	const handleGenerateBatch = async (type: "resolve" | "finalize") => {
		setIsGenerating(true)
		setActiveStep(type)
		setCopySuccess(false)

		try {
			const json = type === "resolve" ? generateResolveBatch() : await generateFinalizeBatch()
			setBatchJson(json)
		} catch (error) {
			console.error("Failed to generate batch:", error)
			setBatchJson(null)
		}
		setIsGenerating(false)
	}

	const handleCopy = async () => {
		if (batchJson) {
			await navigator.clipboard.writeText(batchJson)
			setCopySuccess(true)
			setTimeout(() => setCopySuccess(false), 2000)
		}
	}

	const handleDownload = () => {
		if (batchJson) {
			const blob = new Blob([batchJson], { type: "application/json" })
			const url = URL.createObjectURL(blob)
			const a = document.createElement("a")
			a.href = url
			a.download = `safe-batch-${activeStep}-${Date.now()}.json`
			document.body.appendChild(a)
			a.click()
			document.body.removeChild(a)
			URL.revokeObjectURL(url)
		}
	}

	return (
		<div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-4">
			<div className="mb-4 flex items-center gap-2">
				<div className="size-2 animate-pulse rounded-full bg-blue-500" />
				<h3 className="font-semibold text-blue-400">Safe Multisig Mode</h3>
			</div>

			<div className="mb-4 grid gap-3 sm:grid-cols-2">
				<button
					className={`rounded-lg border p-4 text-left transition ${
						activeStep === "resolve"
							? "border-gray-500 bg-gray-500/10"
							: "border-gray-700 bg-gray-800/50 hover:border-gray-600"
					}`}
					disabled={isGenerating}
					onClick={() => handleGenerateBatch("resolve").catch(err => toast.error(err.message))}
					type="button"
				>
					<div className="mb-1 flex items-center gap-2">
						<span className="flex size-6 items-center justify-center rounded-full bg-gray-500/20 font-bold text-gray-400 text-xs">
							1
						</span>
						<span className="font-medium">Resolve Markets</span>
					</div>
					<p className="text-gray-500 text-xs">Generate batch to resolve all markets</p>
				</button>

				<button
					className={`rounded-lg border p-4 text-left transition ${
						activeStep === "finalize"
							? "border-gray-500 bg-gray-500/10"
							: "border-gray-700 bg-gray-800/50 hover:border-gray-600"
					}`}
					disabled={isGenerating}
					onClick={() => handleGenerateBatch("finalize").catch(err => toast.error(err.message))}
					type="button"
				>
					<div className="mb-1 flex items-center gap-2">
						<span className="flex size-6 items-center justify-center rounded-full bg-gray-500/20 font-bold text-gray-400 text-xs">
							2
						</span>
						<span className="font-medium">Finalize Markets</span>
					</div>
					<p className="text-gray-500 text-xs">Generate batch to finalize (after resolve)</p>
				</button>
			</div>

			{isGenerating && (
				<div className="flex items-center justify-center gap-2 py-8 text-gray-400">
					<div className="size-4 animate-spin rounded-full border-2 border-gray-600 border-t-gray-500" />
					<span>Generating batch...</span>
				</div>
			)}

			{batchJson && !isGenerating && (
				<div className="space-y-3">
					{activeStep === "finalize" && (
						<div className="mb-3">
							<label className="mb-1 block text-gray-400 text-xs" htmlFor="nonce-override">
								Nonce to finalize
							</label>
							<div className="flex items-center gap-2">
								<input
									className="w-20 rounded border border-gray-600 bg-gray-800 px-2 py-1 text-center text-sm"
									id="nonce-override"
									min={1}
									onChange={e => {
										const val = Number.parseInt(e.target.value, 10)
										setNonceOverride(Number.isNaN(val) ? null : val)
									}}
									placeholder="Auto"
									type="number"
									value={nonceOverride ?? ""}
								/>
								<button
									className="text-gray-400 text-xs hover:text-gray-300"
									onClick={() =>
										handleGenerateBatch("finalize").catch(err => toast.error(err.message))
									}
									type="button"
								>
									Regenerate
								</button>
							</div>
						</div>
					)}

					<div className="rounded-lg border border-gray-700 bg-gray-900 p-3">
						<div className="mb-2 flex items-center justify-between">
							<span className="text-gray-400 text-xs">
								{activeStep === "resolve" ? "Resolve" : "Finalize"} Batch Ready
							</span>
							<span className="rounded bg-green-500/20 px-2 py-0.5 text-green-400 text-xs">
								{preview?.markets?.length} operations
							</span>
						</div>
						<pre className="max-h-32 overflow-auto rounded bg-gray-950 p-2 font-mono text-gray-400 text-xs">
							{batchJson.slice(0, 200)}...
						</pre>
					</div>

					<div className="flex flex-wrap gap-2">
						<button
							className={`flex items-center gap-2 rounded-lg px-4 py-2 font-medium text-sm ${
								copySuccess ? "bg-green-600 text-white" : "bg-gray-600 text-white hover:bg-gray-500"
							}`}
							onClick={() => handleCopy().catch(err => toast.error(err.message))}
							type="button"
						>
							{copySuccess ? (
								<>
									<CheckCircle2 aria-hidden="true" className="size-4" /> Copied!
								</>
							) : (
								<>
									<Clipboard aria-hidden="true" className="size-4" /> Copy JSON
								</>
							)}
						</button>
						<button
							className="flex items-center gap-2 rounded-lg bg-gray-700 px-4 py-2 font-medium text-sm text-white hover:bg-gray-600"
							onClick={handleDownload}
							type="button"
						>
							<Download aria-hidden="true" className="size-4" /> Download
						</button>
						<button
							className="flex items-center gap-2 rounded-lg bg-gray-700 px-4 py-2 font-medium text-sm text-white hover:bg-gray-600"
							onClick={() => window.open("https://app.safe.global/", "_blank")}
							type="button"
						>
							<ExternalLink aria-hidden="true" className="size-4" /> Open Safe App
						</button>
					</div>
				</div>
			)}
		</div>
	)
}

// ============================================================================
// Shared Components
// ============================================================================

function StatusBadge({ status }: { status: string }) {
	const colors: Record<string, string> = {
		pending: "bg-yellow-500/20 text-yellow-400",
		active: "bg-green-500/20 text-green-400",
		closed: "bg-orange-500/20 text-orange-400",
		resolved: "bg-blue-500/20 text-blue-400",
		finalized: "bg-gray-500/20 text-gray-400",
		invalid: "bg-red-500/20 text-red-400",
	}

	return (
		<span
			className={cn(
				"rounded px-2 py-0.5 text-xs",
				colors[status] ?? "bg-gray-500/20 text-gray-400",
			)}
		>
			{status}
		</span>
	)
}
